import React from 'react'

import { useStableHandler } from '@sketch/utils'
import {
  EntityOverlayProps,
  TouchableClickEvent,
} from '../components/EntityOverlay'
import { OverlayEntity } from '../utils'
import type {
  CanvasEntityKind,
  EntityInteractions,
  EntityOverlayMode,
} from './types'

type Options = {
  /** Whether this entity is a frame or a Section, resolved by the caller from the node it already holds. */
  kind: CanvasEntityKind
  mode: EntityOverlayMode
  isSelected: boolean
  isHovered: boolean
  interactions: EntityInteractions
}

/**
 * The interaction props for one entity's overlay, in whichever mode the canvas
 * is in.
 *
 * This used to be two hooks, `useViewModeOverlayProps` and
 * `useInspectModeOverlayProps`, both of which ran on every entity and every
 * render with one of the two results thrown away - the rules of hooks leave no
 * choice while they are separate functions. Merging them also puts the
 * difference between the modes in one place instead of two files.
 */
export function useEntityOverlayProps(
  entity: OverlayEntity,
  { kind, mode, isSelected, isHovered, interactions }: Options
): Partial<EntityOverlayProps> {
  const { setHoveredEntityUUID, selectEntity, navigateToEntity } = interactions

  const handleSelectEntity = useStableHandler(
    (e?: TouchableClickEvent<HTMLDivElement>) => {
      // While inspecting, this click would otherwise bubble to the canvas,
      // whose scene hit-test would clear the selection we just made.
      e?.stopPropagation()

      if (!isSelected) {
        selectEntity(entity.objectId, kind)
      }
    }
  )

  const doubleClickHandler = useStableHandler(() => {
    navigateToEntity(entity.objectId, kind, 'doubleClick')
  })

  const handleCtaClick = useStableHandler(
    (e: TouchableClickEvent<HTMLButtonElement>) => {
      e.stopPropagation()

      // Enter on the selected entity reaches its CTA as a click, because the
      // CTA holds focus while selected. `detail` is 0 only when the browser
      // synthesised the click from a key press, which is what keeps the two
      // apart in analytics now that Enter no longer has a listener of its own.
      const isKeyboardActivation = 'detail' in e && e.detail === 0

      navigateToEntity(
        entity.objectId,
        kind,
        isKeyboardActivation ? 'keyPressEnter' : 'CTA'
      )
    }
  )

  // MouseMove rather than MouseEnter/MouseLeave: the canvas's own mousemove
  // handler is still running underneath and would clear the hover we set here,
  // so we stop this one and let the canvas handle leaving the header.
  const handleHeaderMouseMove = useStableHandler(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()

      if (!isHovered) {
        setHoveredEntityUUID(entity.objectId)
      }
    }
  )

  if (mode === 'disabled') {
    return { selected: isSelected, disableAutoHover: true }
  }

  // Note: handlers are left undefined rather than no-op'd where they aren't
  // wanted - `EntityOverlay` reads their presence to decide whether the cursor
  // becomes a pointer.
  if (mode === 'inspect') {
    return {
      selected: isSelected,
      // The scene hit-test owns hover; CSS `:hover` would light up the whole
      // entity whenever the pointer is inside its box, which is the wrong
      // question here.
      disableAutoHover: true,
      // No body handlers: mousemove and click must reach the canvas.
      onFrameHeaderClick: !isSelected ? handleSelectEntity : undefined,
      onFrameHeaderMouseMove: handleHeaderMouseMove,
      onCtaClick: isSelected ? handleCtaClick : undefined,
    }
  }

  return {
    selected: isSelected,
    disableAutoHover: false,
    onOverlayClick: !isSelected ? handleSelectEntity : undefined,
    onOverlayDoubleClick: doubleClickHandler,
    onCtaClick: isSelected ? handleCtaClick : undefined,
  }
}
